import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Instagram, Clock, Check, Loader2 } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { Reveal } from "../hooks/useReveal";
import { useToast } from "../hooks/use-toast";
import { BRAND, HOURS, IMAGES } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "General Inquiry", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast({ title: "Message Sent", description: "Thank you for reaching out. Our team will reply within 24 hours." });
      setForm({ name: "", email: "", phone: "", type: "General Inquiry", message: "" });
    } catch (err) {
      toast({ title: "Submission Failed", description: `Please call us at ${BRAND.phone}.`, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-nala-charcoal border border-nala-gold/25 text-nala-cream px-4 py-3 focus:outline-none focus:border-nala-gold transition-colors placeholder:text-nala-cream-muted/50";

  return (
    <div className="pt-28 pb-24 bg-nala-black min-h-screen">
      <div className="relative h-64 md:h-80 overflow-hidden mb-16">
        <img src={IMAGES.ckSuperior4} alt="Contact Nala" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-nala-black via-nala-black/60 to-nala-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="text-nala-gold tracking-luxe uppercase text-sm mb-3">Get In Touch</p>
          <h1 className="font-display text-5xl md:text-7xl text-gold-gradient">Contact Us</h1>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14">
        {/* Info */}
        <Reveal className="space-y-8">
          <div>
            <h3 className="font-display text-2xl text-gold-gradient mb-6">Visit or Reach Out</h3>
            <ul className="space-y-6 text-nala-cream">
              <li className="flex items-start gap-4">
                <MapPin className="text-nala-gold mt-1" size={20} />
                <div><p className="text-sm uppercase tracking-wide-lux text-nala-gold mb-1">Address</p><p className="font-serif-lux text-lg">{BRAND.address}</p></div>
              </li>
              <li className="flex items-start gap-4">
                <Phone className="text-nala-gold mt-1" size={20} />
                <div><p className="text-sm uppercase tracking-wide-lux text-nala-gold mb-1">Phone</p><a href={BRAND.phoneHref} className="font-serif-lux text-lg hover:text-nala-gold transition-colors">{BRAND.phone}</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Mail className="text-nala-gold mt-1" size={20} />
                <div><p className="text-sm uppercase tracking-wide-lux text-nala-gold mb-1">Email</p><a href={`mailto:${BRAND.email}`} className="font-serif-lux text-lg hover:text-nala-gold transition-colors break-all">{BRAND.email}</a></div>
              </li>
              <li className="flex items-start gap-4">
                <Clock className="text-nala-gold mt-1" size={20} />
                <div>
                  <p className="text-sm uppercase tracking-wide-lux text-nala-gold mb-2">Hours</p>
                  {HOURS.map((h) => <p key={h.day} className="font-serif-lux text-lg">{h.day}: <span className="text-nala-cream-muted">{h.time}</span></p>)}
                </div>
              </li>
            </ul>
            <div className="flex gap-3 mt-8">
              <a href={BRAND.social.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full border border-nala-gold/40 flex items-center justify-center text-nala-gold hover:bg-nala-gold hover:text-nala-black transition-all">
                <Instagram size={18} />
              </a>
            </div>
          </div>
          <div className="border border-nala-gold/20 overflow-hidden h-64">
            <iframe title="Map" src="https://www.google.com/maps?q=4183+Roswell+Rd+NE+Atlanta+GA+30342&output=embed" className="w-full h-full grayscale-[0.3]" loading="lazy" />
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={120}>
          <form onSubmit={handleSubmit} className="border border-nala-gold/20 bg-nala-charcoal/50 p-8 md:p-10">
            <h3 className="font-display text-2xl text-nala-cream mb-2">Send a Message</h3>
            <p className="text-nala-cream-muted text-sm mb-7">Questions, catering, careers or private events — we'd love to hear from you.</p>
            {sent && (
              <div className="flex items-center gap-3 bg-nala-gold/10 border border-nala-gold/40 text-nala-gold px-4 py-3 mb-5 text-sm">
                <Check size={16} /> Message received — we'll reply within 24 hours.
              </div>
            )}
            <div className="space-y-4">
              <input required placeholder="Name *" value={form.name} onChange={update("name")} className={inputClass} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input required type="email" placeholder="Email *" value={form.email} onChange={update("email")} className={inputClass} />
                <input placeholder="Phone" value={form.phone} onChange={update("phone")} className={inputClass} />
              </div>
              <select value={form.type} onChange={update("type")} className={inputClass}>
                <option>General Inquiry</option><option>Catering</option><option>Private Event</option>
                <option>VIP Guest List</option><option>Careers</option>
              </select>
              <textarea required rows={5} placeholder="Message *" value={form.message} onChange={update("message")} className={inputClass} />
            </div>
            <button type="submit" disabled={loading} className="btn-gold w-full mt-6 disabled:opacity-70"><span className="flex items-center gap-2">{loading && <Loader2 size={15} className="animate-spin" />}{loading ? "Sending..." : "Send Message"}</span></button>
            <p className="text-nala-cream-muted/60 text-xs text-center mt-4 tracking-wide-lux">We'll never share your details. Typical reply within 24 hours.</p>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
